import { CheckCircle, X, RefreshCw } from "lucide-react";

export default function TransactionSummaryStep({ 
  step, 
  transactionSummary, 
  summaryLoading, 
  onReset 
}) {
  if (step !== 3) return null;

  if (summaryLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 max-w-md mx-auto">
        <div className="flex items-center justify-center py-6 sm:py-8">
          <div className="w-6 h-6 sm:w-8 sm:h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="ml-3 text-gray-600 text-sm sm:text-base">Loading transaction summary...</span>
        </div>
      </div>
    );
  }

  if (!transactionSummary) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 max-w-md mx-auto">
        <div className="text-center py-6 sm:py-8">
          <X className="h-10 w-10 sm:h-12 sm:w-12 text-red-500 mx-auto mb-3 sm:mb-4" />
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-2">
            Unable to load transaction summary
          </h3>
          <p className="text-gray-600 mb-3 sm:mb-4 text-sm sm:text-base px-2">
            There was an error loading the transaction details.
          </p>
          <button
            onClick={onReset}
            className="theme-button-gradient px-4 sm:px-6 py-2 sm:py-2 rounded-lg text-sm sm:text-base min-h-[44px] touch-manipulation"
          >
            Start New Transaction
          </button>
        </div>
      </div>
    );
  }

  const isCompleted = transactionSummary.status === 'FULFILLED';
  const isCancelled = transactionSummary.status === 'CANCELLED';

  // For cancelled transactions, show simple message
  if (isCancelled) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 max-w-md mx-auto">
        <div className="text-center py-6 sm:py-8">
          <X className="h-12 w-12 sm:h-16 sm:w-16 text-red-500 mx-auto mb-3 sm:mb-4" />
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
            Transaction Cancelled
          </h2>
          <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base px-2">
            The transaction has been cancelled.
          </p>
          <button
            onClick={onReset}
            className="theme-button-gradient px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg flex items-center space-x-2 mx-auto min-h-[44px] touch-manipulation"
          >
            <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5" />
            <span className="text-sm sm:text-base">Start New Transaction</span>
          </button>
        </div>
      </div>
    );
  }

  // For completed transactions, show the simple success design
  return (
    <div className="bg-gradient-to-b from-green-500 to-green-600 rounded-lg p-3 sm:p-4 md:p-6 text-white max-w-sm sm:max-w-md mx-auto">
      {/* Header */}
      <div className="text-center mb-4 sm:mb-6 md:mb-8">
        <h2 className="text-lg sm:text-xl md:text-2xl font-light mb-3 sm:mb-4 md:mb-6">Deal Successful!</h2>
        
        {/* Success Icon with Animation */}
        <div className="relative mx-auto mb-3 sm:mb-4 md:mb-6">
          <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 bg-white rounded-full flex items-center justify-center mx-auto">
            {/* Animated dots around the circle */}
            <div className="absolute inset-0 rounded-full">
              {[...Array(12)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 sm:w-1.5 sm:h-1.5 md:w-2 md:h-2 bg-green-500 rounded-full"
                  style={{
                    top: '50%',
                    left: '50%',
                    transform: `translate(-50%, -50%) rotate(${i * 30}deg) translateY(-28px)`,
                    animationDelay: `${i * 0.1}s`
                  }}
                />
              ))}
            </div>
            <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 bg-green-500 rounded-full flex items-center justify-center">
              <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 md:h-8 md:w-8 text-white" />
            </div>
          </div>
        </div>

        {/* Business Info */}
        <div className="mb-3 sm:mb-4 md:mb-6 px-1 sm:px-2">
          <h3 className="text-base sm:text-lg md:text-xl font-medium leading-tight">
            {transactionSummary.business?.business_name || 'Business Name'}
          </h3>
          <p className="text-green-100 text-xs sm:text-sm mt-1 leading-relaxed">
            {transactionSummary.business?.address || 'Address'}
          </p>
        </div>

        {/* Date and Transaction ID */}
        <div className="text-green-100 text-xs sm:text-sm space-y-1 mb-4 sm:mb-6 md:mb-8 px-1 sm:px-2">
          <p className="leading-relaxed">On {new Date(transactionSummary.created_at).toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short', 
            year: 'numeric'
          })} at {new Date(transactionSummary.created_at).toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          })}</p>
          <p className="font-mono text-xs sm:text-sm">Transaction Id: {transactionSummary.uuid.slice(0, 10)}</p>
        </div>
      </div>

      {/* Bill Details Card */}
      <div className="bg-white rounded-2xl p-3 sm:p-4 md:p-6 text-gray-900 mb-4 sm:mb-6 mx-1 sm:mx-2 md:mx-0">
        <div className="space-y-3 sm:space-y-4">
          {/* Bill Amount */}
          <div className="flex justify-between items-center">
            <span className="text-sm sm:text-base md:text-lg font-semibold">Bill Amount :</span>
            <span className="text-sm sm:text-base md:text-lg font-semibold">
              {transactionSummary.financial_summary?.billable_amount?.formatted || 
               `₹${parseFloat(transactionSummary.billable_amount || 0).toLocaleString('en-IN')}`}
            </span>
          </div>

          {/* Discount (if any) */}
          {transactionSummary.financial_summary?.customer_savings && (
            <div className="flex justify-between items-center text-green-600">
              <span className="text-xs sm:text-sm flex-shrink-0 pr-2">
                Discount to Customer:
              </span>
              <div className="text-right flex-shrink-0">
                <span className="text-xs sm:text-sm font-medium block">
                  - {transactionSummary.financial_summary.customer_savings.formatted}
                </span>
                {/* {transactionSummary.financial_summary.customer_savings.percentage && (
                  <span className="text-xs opacity-75 block">
                    ({transactionSummary.financial_summary.customer_savings.percentage})
                  </span>
                )} */}
              </div>
            </div>
          )}

          {/* Total Payable */}
          <div className="bg-green-500 text-white rounded-full py-3 sm:py-3 md:py-4 px-4 sm:px-6 text-center">
            <div className="text-sm sm:text-base font-semibold">
              Total Payable {transactionSummary.financial_summary?.final_amount?.formatted || 
               transactionSummary.financial_summary?.billable_amount?.formatted ||
               `₹${parseFloat(transactionSummary.billable_amount || 0).toLocaleString('en-IN')}`}
            </div>
          </div>
        </div>
      </div>

      {/* New Transaction Button */}
      <div className="text-center px-1 sm:px-2 md:px-0">
        <button
          onClick={onReset}
          className="bg-white text-green-600 px-4 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2 mx-auto min-h-[44px] touch-manipulation text-sm sm:text-base"
        >
          <RefreshCw className="h-4 w-4 sm:h-5 sm:w-5" />
          <span>Start New Transaction</span>
        </button>
      </div>
    </div>
  );
}